/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Encloses all sub tabs & actions that need to rendered on dedicated delivery flow.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */

import { Box, Tab, Tabs, Typography } from "@mui/material";
import { addDays } from "date-fns";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RtsAdminPanel from "./RtsAdmin/RtsAdminPanel";
import ClientGeneral from "./general/ClientGeneral";
import ClientRoute from "./route/ClientRoute";
import ClientStore from "./store/ClientStore";

function a11yProps(index: number) {
  return {
    id: `horizotal-tab-${index}`,
    "aria-controls": `horizotal-tabpanel-${index}`,
  };
}

// type used in TabPanel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel reusable component
function TabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`horizotal-tabpanel-${index}`}
      aria-labelledby={`horizotal-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ paddingX: 3, paddingBottom: 3 }}>{children}</Box>
      )}
    </div>
  );
}

// All action tab element to be rendered inside dedicated delivery navigation
const DedicatedDeliveryActions = (props: any) => {
  // add state variable for use
  const navigate = useNavigate();
  const location = useLocation().search;
  const [value, setValue] = useState(0);

  // get values from query param
  const clientId = new URLSearchParams(location).get("client_id");
  const clientName = new URLSearchParams(location).get("client_name");
  const startDate = new URLSearchParams(location).get("start_date");
  const endDate = new URLSearchParams(location).get("end_date");

  // All action tab element to be rendered inside dedicated delivery navigation
  useEffect(() => {
    document.title = "Dedicated Delivery - Dashboard";
    if (clientId === null) {
      navigate("../dedicated-delivery");
    }
  });

  return (
    <>
      <Tabs
        orientation="horizontal"
        variant="fullWidth"
        value={value}
        onChange={(e, v) => setValue(v)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="General" {...a11yProps(0)} />
        <Tab label="Stores" {...a11yProps(1)} />
        <Tab label="Data" {...a11yProps(2)} />
        <Tab label="RTS" {...a11yProps(3)} />
        <Tab label="History" {...a11yProps(4)} />
      </Tabs>
      <TabPanel value={value} index={0}>
        <ClientGeneral clientId={Number(clientId)} />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <ClientStore
          clientId={clientId === null ? -1 : +clientId}
          clientName={clientName ?? ""}
          startDate={
            startDate === null ? addDays(new Date(), -30) : new Date(startDate)
          }
          endDate={
            endDate === null ? addDays(new Date(), -30) : new Date(endDate)
          }
        />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <ClientRoute
          clientId={clientId === null ? -1 : +clientId}
          clientName={clientName ?? ""}
          startDate={
            startDate === null ? addDays(new Date(), -30) : new Date(startDate)
          }
          endDate={
            endDate === null ? addDays(new Date(), -30) : new Date(endDate)
          }
        />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <RtsAdminPanel
          clientId={clientId === null ? -1 : +clientId}
          clientName={clientName ?? ""}
          startDate={
            startDate === null ? addDays(new Date(), -30) : startDate
          }
          endDate={
            endDate === null ? addDays(new Date(), -30) : endDate
          }
        />
      </TabPanel>
      <TabPanel value={value} index={4}>
        <Typography component="h4" variant="h3" paddingY={2}>
          coming soon...
        </Typography>
      </TabPanel>
    </>
  );
};
export default DedicatedDeliveryActions;
